import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { trackEvent } from '~/api/analytics';
import { Box, CustomSpinner, DataGrid, Pagination, Text } from '~/commonComponents';
import { DashboardJobsStatus, DashboardJobsType, IDashboardJob } from '~/domain/DashboardJobs';
import { useUserSelector } from '~/hooks';
import { FilterStatusEnum } from '~/utils/Enum';
import { useJobList } from '../../hooks';
import { DataGridCommonColumn } from '../DataGrid.column';
import { EmptyMessage } from '../EmptyMessage';

interface IScheduledTabProps {
  jobType: DashboardJobsType;
}

export const ScheduledTab = ({ jobType }: IScheduledTabProps) => {
  const currentUser = useUserSelector();
  const navigate = useNavigate();

  const { data, isInitialLoading, isLoading, methods, pagination } = useJobList({
    status: DashboardJobsStatus.FILLED,
    jobType,
  });

  const handleRowClick = useCallback(
    (item: IDashboardJob) => {
      trackEvent('Shifts - Scheduled Shifts Tab - Shift Tile Clicked', {
        shiftId: item?.id,
        shiftSpecialty: item?.specialty,
        procedures: item?.procedures,
        breakTime: item?.lunchBreakTime,
        autoConfirm: item?.autoFill,
        softwares: currentUser?.office_info?.softwares,
        assistedHygiene: item?.isAssistedHygiene,
        tpp: item?.amount,
        professionalId: item?.professional?.id,
        dateTimeConfirmed: item?.jobFilledDatetime,
      });

      navigate(`/dashboard/job/${item.id}`);
    },
    [currentUser?.office_info?.softwares, navigate],
  );

  if (isInitialLoading) {
    return (
      <Box marginTop={32}>
        <CustomSpinner isLoading size={24} />
      </Box>
    );
  }

  return (
    <DataGrid
      EmptyComponent={<EmptyMessage type={FilterStatusEnum.SCHEDULED} />}
      PaginationComponent={
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={methods.setCurrentPage}
          maxButtons={5}
        />
      }
      columns={[
        DataGridCommonColumn.Office,
        {
          ...DataGridCommonColumn.Type,
          width: '160px',
        },
        {
          key: 'professional',
          header: 'Professional',
          width: '180px',
          render: (item) => (
            <Text bold textOverflow="ellipsis" overflow="hidden">
              {item.professional
                ? [item.professional.firstName, item.professional.lastName].join(' ')
                : ''}
            </Text>
          ),
        },
        {
          ...DataGridCommonColumn.Details,
          width: '200px',
        },
        DataGridCommonColumn.Profession,
        DataGridCommonColumn.Rate,
        DataGridCommonColumn.Action,
      ]}
      data={data}
      isLoading={isLoading}
      keyExtractor={(item) => item.id}
      onRowClick={handleRowClick}
    />
  );
};
